import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Service } from '@/types/services';
import { getCategoryLabel } from './ServiceCategories';
import { getTechnologyIconsByCategory } from '@/components/TechnologyIconsList';

/** Unsplash originals are multi-megabyte; ask for a card-sized crop instead. */
export const serviceImage = (src: string, width = 800) =>
  src.startsWith('https://images.unsplash.com/') && !src.includes('?')
    ? `${src}?auto=format&fit=crop&w=${width}&q=70`
    : src;

export const serviceStack = (service: Service) =>
  service.stack ?? getTechnologyIconsByCategory(service.category).map(t => t.label);

interface ServiceCardProps {
  service: Service;
}

const ServiceCard = ({ service }: ServiceCardProps) => {
  const IconComponent = LucideIcons[service.icon as keyof typeof LucideIcons] as LucideIcon | undefined;
  const stack = serviceStack(service);

  return (
    <Link
      to={service.customLink || `/services/${service.id}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white/80 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/10 dark:bg-white/[0.03]"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={serviceImage(service.image)}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-900 backdrop-blur dark:bg-slate-950/70 dark:text-white">
          {getCategoryLabel(service.category)}
        </span>
        {IconComponent && (
          <div className="absolute bottom-4 left-4 grid h-11 w-11 place-items-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/40">
            <IconComponent size={22} />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="text-lg font-semibold leading-snug tracking-tight text-slate-900 dark:text-white">
          {service.title}
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {service.shortDescription}
        </p>

        <ul className="mt-5 flex flex-wrap gap-1.5" aria-label="Skills">
          {stack.slice(0, 4).map(skill => (
            <li
              key={skill}
              className="rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-600 dark:border-white/10 dark:text-slate-300"
            >
              {skill}
            </li>
          ))}
          {stack.length > 4 && (
            <li className="px-1 py-0.5 text-xs text-slate-400">+{stack.length - 4}</li>
          )}
        </ul>

        <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-medium text-primary">
          View details
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

export default ServiceCard;
